<template>
  <div class="grid grid-cols-1 gap-4 w-full shrink-0">
    <UFormField label="Identificação de usuário" name="username" size="lg"
      :error="r$.username?.$error ? r$.username.$errors?.[0] : undefined">
      <UInput v-model="state.username" placeholder="Nome de usuário exclusivo (Ex.: nome.sobrenome)" class="w-full"
        size="lg" :color="r$.username?.$dirty ? (r$.username.$correct ? 'success' : 'error') : 'neutral'"
        @blur="r$.username?.$touch()" data-cy="register-username-input">
        <template #trailing>
          <UIcon v-if="r$.username?.$correct" name="i-lucide-check-circle" class="size-5 text-success shrink-0 me-2"
            aria-hidden="true" />
        </template>
      </UInput>
    </UFormField>

    <div class="grid grid-cols-2 gap-4">
      <UFormField label="E-mail" name="email" size="lg" :error="r$.email?.$error ? r$.email.$errors?.[0] : undefined">
        <UInput v-model="state.email" type="email" placeholder="seu@email.com" class="w-full" size="lg" data-cy="register-email-input"
          :color="r$.email?.$dirty ? (r$.email.$correct ? 'success' : 'error') : 'neutral'" @blur="r$.email?.$touch()">
          <template #trailing>
            <UIcon v-if="r$.email?.$correct" name="i-lucide-check-circle" class="size-5 text-success shrink-0 me-2"
              aria-hidden="true" />
          </template>
        </UInput>
      </UFormField>
      <UFormField label="Confirmar E-mail" name="confirmEmail" size="lg" :error="confirmEmailError">
        <UInput v-model="state.confirmEmail" type="email" placeholder="Repita o e-mail" class="w-full" size="lg"
          :disabled="!r$.email?.$correct" data-cy="register-confirmEmail-input"
          :color="r$.email?.$correct && confirmEmailDirty ? (confirmEmailError ? 'error' : 'success') : 'neutral'"
          @update:model-value="confirmEmailDirty = true"
          @blur="confirmEmailDirty = true">
          <template #trailing>
            <UIcon v-if="r$.email?.$correct && confirmEmailDirty && !confirmEmailError" name="i-lucide-check-circle"
              class="size-5 text-success shrink-0 me-2" aria-hidden="true" />
          </template>
        </UInput>
      </UFormField>
    </div>

    <div class="grid grid-cols-2 gap-4">
      <UFormField label="Senha" name="password" size="lg"
        :error="r$.password?.$error ? r$.password.$errors?.[0] : undefined">
        <UInput v-model="state.password" type="password" placeholder="••••••••" class="w-full" size="lg"
          :color="r$.password?.$dirty ? (r$.password.$correct ? 'success' : 'error') : 'neutral'"
          @blur="r$.password?.$touch()" data-cy="register-password-input">
          <template #trailing>
            <UIcon v-if="r$.password?.$correct" name="i-lucide-check-circle" class="size-5 text-success shrink-0 me-2"
              aria-hidden="true" />
          </template>
        </UInput>
      </UFormField>
      <UFormField label="Confirmar Senha" name="confirmPassword" size="lg" :error="confirmPasswordError">
        <UInput v-model="state.confirmPassword" type="password" placeholder="••••••••" class="w-full" size="lg"
          :disabled="!r$.password?.$correct" data-cy="register-confirmPassword-input"
          :color="r$.password?.$correct && confirmPasswordDirty ? (confirmPasswordError ? 'error' : 'success') : 'neutral'"
          @update:model-value="confirmPasswordDirty = true"
          @blur="confirmPasswordDirty = true">
          <template #trailing>
            <UIcon v-if="r$.password?.$correct && confirmPasswordDirty && !confirmPasswordError"
              name="i-lucide-check-circle" class="size-5 text-success shrink-0 me-2" aria-hidden="true" />
          </template>
        </UInput>
      </UFormField>
    </div>

    <!-- Password Strength Indicator -->
    <div class="space-y-1">
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Força da senha</span>
        <span class="text-xs font-medium" :class="strengthText.color">{{ strengthText.label }}</span>
      </div>
      <UProgress :model-value="score" :max="4" :color="strengthColor" size="sm" class="animate-none" />

      <ul class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5" aria-label="Requisitos da senha">
        <li v-for="(req, index) in requirements" :key="index"
          class="flex items-center gap-1.5 transition-colors duration-200"
          :class="req.met ? 'text-success' : 'text-gray-400 dark:text-gray-500'">
          <UIcon :name="req.met ? 'i-lucide-check' : 'i-lucide-circle'" class="size-3.5" />
          <span class="text-[11px] leading-none">{{ req.text }}</span>
        </li>
      </ul>
    </div>

    <div class="mt-2 p-3 bg-gray-50 dark:bg-gray-800/30 rounded border border-gray-100 dark:border-gray-700">
      <UFormField name="acceptedTerms" :error="r$.acceptedTerms?.$error ? r$.acceptedTerms.$errors?.[0] : undefined">
        <UCheckbox v-model="state.acceptedTerms" data-cy="register-acceptedTerms-input">
          <template #label>
            <span class="text-xs text-gray-600 dark:text-gray-400">
              Estou ciente e consinto com a coleta e tratamento dos meus dados pessoais para fins de cadastro, em
              conformidade com a <strong>LGPD (Lei nº 13.709/18)</strong>.
            </span>
          </template>
        </UCheckbox>
      </UFormField>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

const store = useRegisterStore()
const state = store.state

const { r$ } = useRegleSchema(state as any, step0Schema, { autoDirty: true })

// Independent dirty tracking — does NOT depend on Regle's $dirty mechanism
const confirmEmailDirty = ref(false)
const confirmPasswordDirty = ref(false)

// Pure computed errors: react in real-time to any change in either field
const confirmEmailError = computed<string | undefined>(() => {
  if (!confirmEmailDirty.value) return undefined
  if (!state.confirmEmail) return 'Confirme seu e-mail'
  if (state.email !== state.confirmEmail) return 'E-mails não coincidem'
  return undefined
})

const confirmPasswordError = computed<string | undefined>(() => {
  if (!confirmPasswordDirty.value) return undefined
  if (!state.confirmPassword) return 'Confirme sua senha'
  if (state.password !== state.confirmPassword) return 'Senhas não coincidem'
  return undefined
})

// When the main email becomes invalid, reset confirmation
watch(() => r$.email.$correct, (emailCorrect) => {
  if (!emailCorrect) {
    confirmEmailDirty.value = false
    state.confirmEmail = ''
  }
})

// When the main password becomes invalid, reset confirmation
watch(() => r$.password.$correct, (passwordCorrect) => {
  if (!passwordCorrect) {
    confirmPasswordDirty.value = false
    state.confirmPassword = ''
  }
})

// Lista visual percorrendo as regras listadas no app/utils/registerSchemas.ts
const requirements = computed(() => {
  const p = state.password || ''
  return passwordRules.map(rule => ({
    text: rule.label,
    met: rule.test(p)
  }))
})

// Calcula o score com base no número total de regras
const score = computed(() => {
  return requirements.value.filter(req => req.met).length
})

// O score máximo agora equivale ao tamanho do array de regras
const strengthColor = computed(() => {
  if (score.value === 0) return 'neutral'
  if (score.value <= 1) return 'error'
  if (score.value <= passwordRules.length - 1) return 'warning'
  return 'success'
})

const strengthText = computed(() => {
  if (score.value === 0) return { label: 'Vazia', color: 'text-gray-400' }
  if (score.value <= 1) return { label: 'Fraca', color: 'text-error' }
  if (score.value <= passwordRules.length - 1) return { label: 'Média', color: 'text-warning' }
  return { label: 'Forte', color: 'text-success' }
})

defineExpose({
  validate: async () => {
    // Force confirm fields visible when clicking "Next"
    if (r$.email.$correct) confirmEmailDirty.value = true
    if (r$.password.$correct) confirmPasswordDirty.value = true

    // Inject cross-field errors into Regle so r$.$validate() blocks progression
    if (confirmEmailError.value) {
      r$.confirmEmail.$setExternalErrors([confirmEmailError.value])
    } else {
      r$.confirmEmail.$clearExternalErrors()
    }
    if (confirmPasswordError.value) {
      r$.confirmPassword.$setExternalErrors([confirmPasswordError.value])
    } else {
      r$.confirmPassword.$clearExternalErrors()
    }

    return await r$.$validate()
  },
  reset: () => r$.$reset()
})
</script>